import { useState } from "react";
import { format } from "date-fns";
import { FiShield, FiShieldOff } from "react-icons/fi";
import { getApiUrl } from "../../../../config/api";
import ConfirmModal from "../../../../components/ConfirmModal";

// The switch that decides whether a user over their weekly quota is actually
// blocked. Usage is recorded either way. Turning it on blocks real users from
// their next turn, so both directions go through a confirmation.
export default function EnforcementCard({
  enforcement,
  error,
  onRetry,
  overQuotaUsers,
  canEdit,
  currentUser,
  onChanged,
  language,
}) {
  const zh = language === "zh";
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const enforced = Boolean(enforcement?.enforced);

  const save = async () => {
    // Close first: the modal has no busy state, and a second click must not
    // send a second request.
    setConfirming(false);
    setSaving(true);
    setSaveError(null);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(
        `${getApiUrl()}/api/v2/usage-billing/admin/enforcement`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ enforced: !enforced }),
        },
      );
      // A proxy error page (a 502 from Cloud Run, say) isn't JSON.
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(data.error || `Request failed (${response.status})`);
      }
      onChanged();
    } catch (err) {
      console.error("Failed to change usage enforcement:", err);
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (error) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        <span>
          {zh ? "無法載入限制開關狀態：" : "Couldn't load the enforcement switch: "}
          {error}
        </span>
        <button
          type="button"
          onClick={onRetry}
          className="shrink-0 font-medium underline"
        >
          {zh ? "重試" : "Retry"}
        </button>
      </div>
    );
  }

  const changed = enforcement?.updated_at
    ? format(
        new Date(enforcement.updated_at),
        zh ? "yyyy/M/d HH:mm" : "d MMM yyyy, HH:mm",
      )
    : null;

  return (
    <div
      className={`rounded-xl border p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 ${
        enforced ? "border-red-200 bg-red-50/60" : "border-gray-200 bg-white"
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`w-10 h-10 shrink-0 rounded-lg flex items-center justify-center ${
            enforced ? "bg-red-100 text-red-600" : "bg-gray-100 text-gray-500"
          }`}
        >
          {enforced ? (
            <FiShield className="w-5 h-5" aria-hidden="true" />
          ) : (
            <FiShieldOff className="w-5 h-5" aria-hidden="true" />
          )}
        </div>
        <div>
          <p className="font-semibold text-gray-900">
            {!enforcement
              ? zh
                ? "正在載入限制開關..."
                : "Loading the enforcement switch..."
              : enforced
                ? zh
                  ? "限制已開啟：用完本週額度的使用者會被封鎖"
                  : "Enforcement is on: users who reach their weekly quota are blocked"
                : zh
                  ? "限制已關閉：只記錄用量，不會封鎖任何人"
                  : "Enforcement is off: usage is recorded, nobody is blocked"}
          </p>
          {enforcement && (
            <p className="text-sm text-gray-500 mt-0.5">
              {changed
                ? zh
                  ? `最後由 ${enforcement.updated_by || "未知"} 於 ${changed} 變更`
                  : `Last changed by ${enforcement.updated_by || "unknown"} on ${changed}`
                : zh
                  ? "尚未變更過（預設為關閉）"
                  : "Never changed (off by default)"}
            </p>
          )}
          {saveError && (
            <p className="text-sm text-red-600 mt-1">
              {zh ? "變更失敗：" : "Couldn't change it: "}
              {saveError}
            </p>
          )}
        </div>
      </div>

      {canEdit && enforcement && (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          disabled={saving}
          className={`shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
            enforced
              ? "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
              : "bg-red-500 text-white hover:bg-red-600"
          }`}
        >
          {saving
            ? zh
              ? "儲存中..."
              : "Saving..."
            : enforced
              ? zh
                ? "關閉限制…"
                : "Turn off…"
              : zh
                ? "開啟限制…"
                : "Turn on…"}
        </button>
      )}

      {confirming && (
        <ConfirmModal
          variant="warning"
          title={
            enforced
              ? zh
                ? "關閉用量限制？"
                : "Turn off enforcement?"
              : zh
                ? "開啟用量限制？"
                : "Turn on enforcement?"
          }
          message={
            enforced
              ? zh
                ? "之後不會封鎖任何人，用量仍會持續記錄。"
                : "Nobody will be blocked any more. Usage keeps being recorded."
              : zh
                ? `用完本週額度的使用者，下一次在 TG PIMS Agent 提問時會被封鎖。${
                    overQuotaUsers !== null
                      ? `目前有 ${overQuotaUsers} 位使用者已超過本週額度。`
                      : ""
                  }開啟前請確認後端的最少執行個體數已設為 1。`
                : `Users who have reached their weekly quota are blocked from their next question in TG PIMS Agent.${
                    overQuotaUsers !== null
                      ? ` ${overQuotaUsers} ${overQuotaUsers === 1 ? "user is" : "users are"} over their quota right now.`
                      : ""
                  } Before turning it on, make sure the backend runs with at least 1 minimum instance.`
          }
          confirmLabel={
            enforced ? (zh ? "關閉" : "Turn off") : zh ? "開啟" : "Turn on"
          }
          cancelLabel={zh ? "取消" : "Cancel"}
          onConfirm={save}
          onCancel={() => setConfirming(false)}
        />
      )}
    </div>
  );
}
